import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { camelCase } from 'lodash';

import StringField from '../../core/components/Fields/StringField.tsx';
import IntegerField from '../../core/components/Fields/IntegerField.tsx';
import DecimalField from '../../core/components/Fields/DecimalField.tsx';
import DateField from '../../core/components/Fields/DateField.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import { ForceSelectionAutoCompleteField } from '../../core/components/Fields/AutoCompleteField.tsx';
import Button from '../../core/components/Button.tsx';
import { WorkistLogo } from '../../core/components/WorkistLogo.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { COUNTRY_CODES } from '../../core/utils';
import { LANGUAGE_OPTIONS } from '../../../constants.ts';
import {
    getFormFieldGroups,
    useDocumentConfig,
    FormField as FormFieldType,
} from '../../document/pages/Assistance/useDocumentConfig.ts';

import { Option } from '../../core/components/Fields/AutoCompleteField.tsx';
import LookupField from '../LookupField.tsx';
import useFieldLabel, { LINE_ITEM_PREFIX } from '../useFieldLabel.ts';
import { computeLookupBackfills } from '../lookupMappings.ts';
import { fetchFormRenderData, FormField as ApiField, FormRenderData, searchLookup, submitForm } from '../api.ts';
import FormNotAvailablePage from './FormNotAvailablePage.tsx';
import ThankYouPage from './ThankYouPage.tsx';

// The customer block (read-only) at the top of the form already covers all
// customer identity. Drop those groups from the order section so we don't
// re-prompt for what's already locked.
const HIDDEN_HEADER_GROUPS = new Set(['customer']);
const HIDDEN_LINE_ITEM_GROUPS = new Set(['customer', 'converted_unit', 'converted_quantity']);

// backend value_type → frontend field component (plain, non-lookup values)
const FIELD_COMPONENTS: Record<string, any> = {
    string: StringField,
    integer: IntegerField,
    float: DecimalField,
    decimal: DecimalField,
    boolean: BooleanField,
    date: DateField,
    datetime: DateField,
};

const CountrySelectField = ({
    value,
    onValueChange,
    required,
}: {
    value: string;
    onValueChange: (value: unknown) => void;
    required?: boolean;
}) => {
    const { t, i18n } = useTranslation('core');

    const options = useMemo(
        () =>
            COUNTRY_CODES.map((countryCode) => ({
                value: countryCode,
                label: countryCode,
                description: i18n.exists(`core:countries.${countryCode}`) ? t(`core:countries.${countryCode}`) : '',
            })),
        [t, i18n]
    );

    const filterOption = useCallback(
        (countryCode: string, search: string, isDirty?: boolean) => {
            const translatedValue = i18n.exists(`core:countries.${countryCode}`)
                ? t(`core:countries.${countryCode}`)
                : '';
            return !isDirty ||
                !search ||
                countryCode.toLowerCase().includes(search.toLowerCase()) ||
                translatedValue.toLowerCase().includes(search.toLowerCase())
                ? 1
                : 0;
        },
        [t, i18n]
    );

    return (
        <ForceSelectionAutoCompleteField
            value={value}
            onValueChange={onValueChange}
            options={options}
            filterOption={filterOption}
            required={required}
        />
    );
};

type LookupFetcher = (
    searchTerm: string,
    config: { lookupDefinitionId: string; lookupFieldName: string; lookupType?: string; partitionId: string | null }
) => Promise<Option[]>;

const normalizeDocumentConfig = (shape: FormRenderData) => ({
    options: [],
    fields: shape.fields.map((f: ApiField) => ({
        name: f.name,
        valueType: f.value_type,
        position: f.position,
        enabled: f.enabled,
        options: f.options.map((o) => ({ name: o.name, value: o.value })),
    })),
});

/**
 * `getFormFieldGroups` mutates `config.isRequired` on each subfield: on a
 * fresh, empty form it downgrades required fields to not-required unless the
 * bare group entry is explicitly `is_required=true` in the channel config,
 * which it usually isn't. For the public form we want the red asterisk to
 * survive that mutation, so we read the *raw* (pre-mutation) flat config
 * emitted by `useDocumentConfig` directly.
 */
type RawConfigMap = Record<string, any>;

const buildRawConfigMap = (fieldConfigsByPrefix: Record<string, any[]>): RawConfigMap => {
    const map: RawConfigMap = {};
    for (const list of Object.values(fieldConfigsByPrefix || {})) {
        for (const cfg of list) {
            if (cfg?.name) map[cfg.name] = cfg;
        }
    }
    return map;
};

const isFieldRequiredRaw = (field: FormFieldType, raw: RawConfigMap): boolean => {
    const name = (field.config as any)?.name;
    if (!name) return false;
    return !!raw[name]?.isRequired;
};

/**
 * A group is considered required if either a bare group-level config entry
 * says so, or ANY subfield is required in the raw config. Mirrors the user's
 * expectation: if the group has required data somewhere, the lead picker
 * should show an asterisk.
 */
const isGroupRequiredRaw = (group: FormFieldType[], raw: RawConfigMap): boolean => {
    if (!group.length) return false;
    const fieldName = group[0].fieldName;
    if (raw[fieldName]?.isRequired) return true;
    return group.some((f) => isFieldRequiredRaw(f, raw));
};

/** Filter out the bare group-level entry when the group has actual subfields. */
const visibleFields = (group: FormFieldType[]) =>
    group.length === 1 ? group : group.filter((f) => (f as any).path !== f.fieldName);

export const LanguageSwitcher = () => {
    const { i18n } = useTranslation();
    const current = i18n.language?.split('-')[0] || 'en';
    return (
        <select
            value={current}
            onChange={(e) => void i18n.changeLanguage(e.target.value)}
            className="bg-surface-weak border border-solid border-line-weak rounded px-2 py-1 text-sm text-content-strong"
            aria-label="Language"
        >
            {LANGUAGE_OPTIONS.map((l) => (
                <option key={l.value} value={l.value}>
                    {l.label}
                </option>
            ))}
        </select>
    );
};

export const LabeledRow = ({
    label,
    required = false,
    children,
    className,
}: {
    label: React.ReactNode;
    required?: boolean;
    children: React.ReactNode;
    className?: string;
}) => (
    <div className={classnames('flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3', className)}>
        <label className="flex gap-1 text-xs text-content-medium sm:w-36 sm:flex-none sm:pt-2">
            <span className="truncate">{label}</span>
            {required && <span className="text-content-danger-strong">*</span>}
        </label>
        <div className="min-w-0 sm:flex-1">{children}</div>
    </div>
);

interface FieldRendererProps {
    field: FormFieldType;
    value: unknown;
    onChange: (value: unknown) => void;
    onSelectOption?: (option: any) => void;
    /**
     * Customer-scoped lookups (addresses, contacts) are filtered by the form's
     * locked client so customers only see their own records. Passed through to
     * `SEARCH_MASTERDATA_ENTITY_FIELD` as `partitionId`. ClientLookupType
     * itself is never partitioned (it IS the partition).
     */
    clientPartitionId: string | null;
    /** Overrides `field.config.isRequired` — e.g. for a composite lead that's
     *  required via its group even if the lead subfield itself isn't. */
    requiredOverride?: boolean;
    /** Raw (pre-mutation) flat config map used for reliable isRequired reads. */
    rawConfigMap: RawConfigMap;
    choicesOverrides?: Record<string, string[]>;
    lookupFetcher: LookupFetcher;
    labelSection?: 'headerView' | 'itemsView';
}

const FieldRenderer = ({
    field,
    value,
    onChange,
    onSelectOption,
    clientPartitionId,
    requiredOverride,
    rawConfigMap,
    choicesOverrides,
    lookupFetcher,
    labelSection = 'headerView',
}: FieldRendererProps) => {
    const label = useFieldLabel(labelSection);
    const config = field.config as any;

    const performLookups = !!config.performLookups;
    const lookupDefinitionId = config.lookupDefinitionId as string | undefined;
    const lookupFieldName = config.lookupFieldName as string | undefined;
    const lookupType = config.lookupType as string | undefined;

    const required = requiredOverride ?? isFieldRequiredRaw(field, rawConfigMap);
    // Prefer a tenant-authored custom label (fieldLabelName) over the i18n
    // key, matching Assistance's `field.config?.fieldLabelName ?? t(...)`.
    const resolvedLabel = config.fieldLabelName || label(config.name || field.fieldName);
    const stringValue = (value ?? '') as string;

    const fetchOptions = useCallback(
        (searchTerm) =>
            lookupFetcher(searchTerm, {
                lookupDefinitionId: lookupDefinitionId!,
                lookupFieldName: lookupFieldName!,
                lookupType,
                partitionId: lookupType === 'ClientLookupType' ? null : clientPartitionId,
            }),
        [lookupFetcher, lookupDefinitionId, lookupFieldName, lookupType, clientPartitionId]
    );

    let content: React.ReactNode;

    if (performLookups && lookupDefinitionId && lookupFieldName) {
        content = (
            <LookupField
                value={stringValue}
                lookupFieldName={lookupFieldName}
                lookupType={lookupType}
                allowInvalidIds={config.allowInvalidIds !== false}
                onValueChange={onChange}
                onSelect={(option) => {
                    const primary = String(option?.[camelCase(lookupFieldName)] ?? option?.value ?? '');
                    onChange(primary);
                    onSelectOption?.(option);
                }}
                required={required}
                fetchOptions={fetchOptions}
            />
        );
    } else if (field.valueKey === 'country') {
        content = <CountrySelectField value={stringValue} onValueChange={onChange} required={required} />;
    } else {
        // Fields with configured `choices` (e.g. `unit` on line items) render as
        // a select. A dynamic override (e.g. article's `orderUnits` after a pick)
        // takes precedence over the static channel-level choices.
        const override = choicesOverrides?.[config.name];
        const choices = (override && override.length > 0 ? override : (config.choices as unknown[] | undefined)) as
            | unknown[]
            | undefined;

        if (Array.isArray(choices) && choices.length > 0) {
            content = (
                <SelectField
                    value={stringValue}
                    onValueChange={onChange}
                    options={choices.map((c) => ({ label: String(c), value: String(c) }))}
                    required={required}
                />
            );
        } else {
            const Component = FIELD_COMPONENTS[field.valueType] || StringField;
            const maxLength = config.maxFieldLength as number | undefined;
            const dateFormat = config.dateFormat as string | undefined;

            const extraProps: Record<string, unknown> = {};
            if (maxLength && maxLength > 0) extraProps.maxLength = maxLength;
            if (dateFormat) extraProps.format = dateFormat;

            content = <Component value={stringValue} onValueChange={onChange} required={required} {...extraProps} />;
        }
    }

    return (
        <LabeledRow label={resolvedLabel} required={required}>
            {content}
        </LabeledRow>
    );
};

/**
 * Apply a lookup option to the current scope: backfills empty sibling fields
 * via `computeLookupBackfills` (which uses the per-lookup-type mapping tables
 * that mirror Assistance's backend handlers) AND applies dynamic choice
 * overrides when the option has a list of valid choices (e.g. article's
 * `orderUnits` → unit select choices).
 */
const applyLookupToScope = (
    option: any,
    scopeFields: FormFieldType[],
    scopeValues: Record<string, unknown>,
    setScopeValue: (path: string, value: unknown) => void,
    setChoicesOverride?: (fieldKey: string, choices: string[]) => void
) => {
    const { values, unitChoices, unitField } = computeLookupBackfills(option, scopeFields, scopeValues);
    for (const [key, value] of Object.entries(values)) {
        setScopeValue(key, value);
    }
    if (unitChoices && unitField && setChoicesOverride) {
        setChoicesOverride((unitField.config as any).name, unitChoices);
    }
};

interface GroupBlockProps {
    group: FormFieldType[];
    values: Record<string, unknown>;
    setValue: (path: string, value: unknown) => void;
    clientPartitionId: string | null;
    rawConfigMap: RawConfigMap;
    /**
     * Called when a lookup field inside the group is picked — provides the
     * full masterdata option so the outer scope (header or line-item row) can
     * backfill siblings that live in OTHER groups (e.g. article → description,
     * unit).
     */
    onLookupSelected?: (option: any) => void;
    /**
     * When true, render an Assistance-style group heading above the fields.
     * Only used in the header section — line-item rows omit the heading
     * because the row card itself already groups the fields.
     */
    showGroupHeading?: boolean;
    choicesOverrides?: Record<string, string[]>;
    lookupFetcher: LookupFetcher;
    labelSection?: 'headerView' | 'itemsView';
}

const GroupBlock = ({
    group,
    values,
    setValue,
    clientPartitionId,
    rawConfigMap,
    onLookupSelected,
    showGroupHeading = false,
    choicesOverrides,
    lookupFetcher,
    labelSection = 'headerView',
}: GroupBlockProps) => {
    const label = useFieldLabel(labelSection);

    const displayFields = visibleFields(group);

    const leadField = displayFields.find((f) => {
        const cfg = f.config as any;
        return !!cfg.performLookups && cfg.lookupDefinitionId && cfg.lookupFieldName;
    });
    const isComposite = !!leadField && displayFields.length > 1;
    const groupRequired = isGroupRequiredRaw(group, rawConfigMap);
    const groupName = group[0].fieldName;
    const groupLabelText = label(groupName);

    const renderField = (f: FormFieldType, override?: { required?: boolean }) => {
        const key = (f.config as any).name as string;
        return (
            <FieldRenderer
                key={key}
                field={f}
                value={values[key]}
                onChange={(v) => setValue(key, v)}
                onSelectOption={
                    // Any lookup field in the group (not just the composite
                    // lead) forwards its selection to the scope so cross-group
                    // siblings (article_description, unit, …) get filled.
                    (f.config as any).performLookups ? onLookupSelected : undefined
                }
                clientPartitionId={clientPartitionId}
                requiredOverride={override?.required}
                rawConfigMap={rawConfigMap}
                choicesOverrides={choicesOverrides}
                lookupFetcher={lookupFetcher}
                labelSection={labelSection}
            />
        );
    };

    // Every field always renders with its own inline label (Assistance's
    // `AssistanceSection.FieldLabel`). Flat single-field groups in the header
    // therefore show the group heading AND a matching inline field label —
    // the same duplication Assistance shows, by design.
    const fieldNodes =
        isComposite && leadField
            ? [
                  renderField(leadField, {
                      required: groupRequired || isFieldRequiredRaw(leadField, rawConfigMap),
                  }),
                  ...displayFields.filter((f) => f !== leadField).map((f) => renderField(f)),
              ]
            : displayFields.map((f) => renderField(f));

    return (
        <div className="flex flex-col gap-2">
            {showGroupHeading && (
                <div className="text-sm font-medium text-content-strong pt-2 pb-1 flex gap-1">
                    <span>{groupLabelText}</span>
                    {groupRequired && <span className="text-content-danger-strong">*</span>}
                </div>
            )}
            {fieldNodes}
        </div>
    );
};

const PublicFormPage = () => {
    const { token = '' } = useParams();
    const { t } = useTranslation('forms');

    const [shape, setShape] = useState<FormRenderData | null>(null);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [referenceNumber, setReferenceNumber] = useState<string | null>(null);

    const [logoFailed, setLogoFailed] = useState(false);
    const [senderEmail, setSenderEmail] = useState('');
    const [header, setHeader] = useState<Record<string, unknown>>({});
    const [lineItems, setLineItems] = useState<Record<string, unknown>[]>([{}]);
    // Dynamic choices overrides, keyed by scope. Populated when a lookup
    // supplies a list (e.g. article's `orderUnits` → unit field choices).
    const [headerChoicesOverrides, setHeaderChoicesOverrides] = useState<Record<string, string[]>>({});
    const [lineItemChoicesOverrides, setLineItemChoicesOverrides] = useState<Record<number, Record<string, string[]>>>(
        {}
    );

    useEffect(() => {
        const controller = new AbortController();
        fetchFormRenderData(token, controller.signal)
            .then((data) => setShape(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setLoadError(err);
            });
        return () => controller.abort();
    }, [token]);

    const normalized = useMemo(() => (shape ? normalizeDocumentConfig(shape) : null), [shape]);
    const { fieldConfigsByPrefix } = useDocumentConfig(normalized, [LINE_ITEM_PREFIX]);

    const headerGroups: FormFieldType[][] = useMemo(() => {
        if (!fieldConfigsByPrefix) return [];
        const groups = getFormFieldGroups({}, fieldConfigsByPrefix[''] || [], '', undefined);
        return groups.filter((g) => !HIDDEN_HEADER_GROUPS.has(g[0].fieldName));
    }, [fieldConfigsByPrefix]);

    const headerScope = useMemo(() => headerGroups.flatMap(visibleFields), [headerGroups]);

    const lineItemConfigs = fieldConfigsByPrefix?.[LINE_ITEM_PREFIX] || [];
    const hasLineItemFields = lineItemConfigs.length > 0;

    const rawConfigMap = useMemo(() => buildRawConfigMap(fieldConfigsByPrefix || {}), [fieldConfigsByPrefix]);

    const lookupFetcher: LookupFetcher = useCallback(
        async (searchTerm, config) => {
            const { edges } = await searchLookup(token, {
                lookupDefinitionId: config.lookupDefinitionId,
                lookupFieldName: config.lookupFieldName,
                searchTerm,
                partitionId: config.partitionId,
            });
            return (edges || []).map(({ node }: any) => ({
                value: node?.[camelCase(config.lookupFieldName)],
                ...node,
                __typename: config.lookupType,
                data: { _lookupEntity: node },
            }));
        },
        [token]
    );

    if (loadError) return <FormNotAvailablePage />;
    if (!shape) {
        return (
            <div className="w-full h-screen flex items-center justify-center text-content-medium">{t('loading')}</div>
        );
    }
    if (referenceNumber) return <ThankYouPage referenceNumber={referenceNumber} tenantName={shape.tenant.name} />;

    const clientPartitionId = shape.form.client.customer_number || null;

    const setHeaderValue = (key: string, value: unknown) => setHeader((prev) => ({ ...prev, [key]: value }));
    const setLineItemValue = (index: number, key: string, value: unknown) => {
        setLineItems((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [key]: value };
            return next;
        });
    };
    const setHeaderChoicesOverride = (key: string, choices: string[]) =>
        setHeaderChoicesOverrides((prev) => ({ ...prev, [key]: choices }));
    const setLineItemChoicesOverride = (idx: number, key: string, choices: string[]) =>
        setLineItemChoicesOverrides((prev) => ({
            ...prev,
            [idx]: { ...(prev[idx] || {}), [key]: choices },
        }));

    const validateRequired = (): string | null => {
        if (!senderEmail.trim()) return t('errors.missingEmail');

        for (const group of headerGroups) {
            const required = isGroupRequiredRaw(group, rawConfigMap);
            if (!required) continue;
            const filled = group.some((f) => {
                const key = (f.config as any).name;
                const v = header[key];
                return !(v === undefined || v === null || v === '');
            });
            if (!filled) return t('errors.missingRequired', { field: group[0].fieldName });
        }

        if (hasLineItemFields) {
            const lineItemGroups = getFormFieldGroups({}, lineItemConfigs, LINE_ITEM_PREFIX, 0).filter(
                (g) => !HIDDEN_LINE_ITEM_GROUPS.has(g[0].fieldName)
            );
            const anyComplete = lineItems.some((row) => {
                return lineItemGroups.every((g) => {
                    if (!isGroupRequiredRaw(g, rawConfigMap)) return true;
                    return g.some((f) => {
                        const key = (f.config as any).name;
                        const v = row[key];
                        return !(v === undefined || v === null || v === '');
                    });
                });
            });
            if (!anyComplete) return t('errors.missingLineItem');
        }

        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        const err = validateRequired();
        if (err) {
            setSubmitError(err);
            return;
        }
        const cleanedLineItems = lineItems.filter((row) =>
            Object.values(row).some((v) => v !== undefined && v !== null && v !== '')
        );
        try {
            const res = await submitForm(token, {
                sender_email: senderEmail,
                header,
                line_items: cleanedLineItems,
            });
            setReferenceNumber(res.reference_number);
        } catch {
            setSubmitError(t('errors.submitFailed'));
        }
    };

    const { client } = shape.form;

    // `html, body, #root` are all `height: 100%`, so `min-h-screen` on an
    // inner element won't paint its background across an overflowing page.
    // We follow the app's standard pattern (see `Page.Content`): the outer
    // wrapper fills the root exactly, and scrolling happens inside the main
    // body. Header and footer stay flush with their respective backgrounds.
    return (
        <div className="h-full flex flex-col bg-surface-medium text-content-strong">
            <header className="flex-none border-b border-solid border-line-weak bg-surface-weak">
                <div className="max-w-3xl mx-auto flex items-center justify-between py-4 px-6">
                    {shape.tenant.logo_url && !logoFailed ? (
                        <img
                            src={shape.tenant.logo_url}
                            alt={shape.tenant.name}
                            className="h-8 w-auto object-contain"
                            onError={() => setLogoFailed(true)}
                        />
                    ) : (
                        <div className="font-semibold">{shape.tenant.name || shape.channel.name}</div>
                    )}
                    <LanguageSwitcher />
                </div>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto">
                <main className="max-w-3xl mx-auto px-6 py-10">
                    <h1 className="text-2xl font-semibold mb-2">{t('heading', { channel: shape.channel.name })}</h1>
                    {shape.form.intro_text && (
                        <p className="text-content-medium whitespace-pre-wrap mb-6">{shape.form.intro_text}</p>
                    )}

                    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                        {/* Read-only customer block */}
                        <section className="bg-surface-weak border border-solid border-line-weak rounded p-6 flex flex-col gap-3">
                            <h2 className="text-base font-semibold pb-1">{t('sections.customer')}</h2>
                            <LabeledRow label={t('fields.companyName')}>
                                <StringField value={client.name} readOnly disabled />
                            </LabeledRow>
                            <LabeledRow label={t('fields.customerNumber')}>
                                <StringField value={client.customer_number} readOnly disabled />
                            </LabeledRow>
                            <LabeledRow label={t('fields.yourEmail')} required>
                                <StringField
                                    value={senderEmail}
                                    onValueChange={(v: any) => setSenderEmail(String(v ?? ''))}
                                    type="email"
                                    required
                                />
                            </LabeledRow>
                        </section>

                        {/* Header fields — ONE card, group sublabels inside. */}
                        {headerGroups.length > 0 && (
                            <section className="bg-surface-weak border border-solid border-line-weak rounded p-6 flex flex-col gap-3">
                                <h2 className="text-base font-semibold pb-1">{t('sections.order')}</h2>
                                {headerGroups.map((group) => (
                                    <GroupBlock
                                        key={group[0].fieldName}
                                        group={group}
                                        values={header}
                                        setValue={setHeaderValue}
                                        clientPartitionId={clientPartitionId}
                                        rawConfigMap={rawConfigMap}
                                        onLookupSelected={(option) =>
                                            applyLookupToScope(
                                                option,
                                                headerScope,
                                                header,
                                                setHeaderValue,
                                                setHeaderChoicesOverride
                                            )
                                        }
                                        showGroupHeading
                                        choicesOverrides={headerChoicesOverrides}
                                        lookupFetcher={lookupFetcher}
                                    />
                                ))}
                            </section>
                        )}

                        {/* Line items */}
                        {hasLineItemFields && (
                            <section className="flex flex-col gap-4">
                                <h2 className="text-base font-semibold">{t('sections.lineItems')}</h2>

                                {lineItems.map((row, idx) => {
                                    const rowGroups = getFormFieldGroups(
                                        {},
                                        lineItemConfigs,
                                        LINE_ITEM_PREFIX,
                                        idx
                                    ).filter((g) => !HIDDEN_LINE_ITEM_GROUPS.has(g[0].fieldName));
                                    const rowScope = rowGroups.flatMap(visibleFields);
                                    const setRowValue = (key: string, v: unknown) => setLineItemValue(idx, key, v);
                                    return (
                                        <div
                                            key={idx}
                                            className="bg-surface-weak border border-solid border-line-weak rounded p-6 flex flex-col gap-3"
                                        >
                                            {/* Fixed-height row header prevents layout jump when the
                                            Remove button shows/hides as rows are added/removed. */}
                                            <div className="flex items-center justify-between min-h-9">
                                                <h3 className="text-sm font-semibold">
                                                    {t('lineItem.heading', { index: idx + 1 })}
                                                </h3>
                                                {lineItems.length > 1 && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() =>
                                                            setLineItems((prev) => prev.filter((_, i) => i !== idx))
                                                        }
                                                    >
                                                        {t('lineItem.remove')}
                                                    </Button>
                                                )}
                                            </div>

                                            {rowGroups.map((group) => (
                                                <GroupBlock
                                                    key={group[0].fieldName}
                                                    group={group}
                                                    values={row}
                                                    setValue={setRowValue}
                                                    clientPartitionId={clientPartitionId}
                                                    rawConfigMap={rawConfigMap}
                                                    onLookupSelected={(option) =>
                                                        applyLookupToScope(option, rowScope, row, setRowValue, (k, c) =>
                                                            setLineItemChoicesOverride(idx, k, c)
                                                        )
                                                    }
                                                    choicesOverrides={lineItemChoicesOverrides[idx]}
                                                    lookupFetcher={lookupFetcher}
                                                    labelSection="itemsView"
                                                />
                                            ))}
                                        </div>
                                    );
                                })}

                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setLineItems((prev) => [...prev, {}])}
                                >
                                    {t('lineItem.add')}
                                </Button>
                            </section>
                        )}

                        {submitError && <div className="text-content-danger-weak text-sm">{submitError}</div>}

                        <div className="flex justify-end">
                            <Button type="submit" variant="primary" className="w-full px-6 sm:w-auto">
                                {t('submit')}
                            </Button>
                        </div>
                    </form>
                </main>

                <footer className="max-w-3xl mx-auto px-6 py-8 text-content-medium text-xs flex items-center gap-2 justify-center">
                    <span>{t('poweredBy')}</span>
                    <a href="https://workist.com" target="_blank" rel="noopener noreferrer">
                        <WorkistLogo className="h-4 w-auto" />
                    </a>
                </footer>
            </div>
        </div>
    );
};

export default PublicFormPage;
